import styled from 'styled-components'

export const RowWrapper = styled.div<{ $selectionMode?: boolean }>`
  display: grid;

  /* BRAND-9514: selection mode prepends a fixed checkbox column. */
  grid-template-columns:
    ${({ $selectionMode }) => ($selectionMode ? '2rem ' : '')}3.5rem minmax(8rem, 1.3fr)
    minmax(8rem, 1.5fr) minmax(8rem, 1fr) minmax(8rem, 0.5fr)
    minmax(8rem, 0.5fr) auto 1fr;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: ${({ theme }) => theme.whiteColor};
  border-radius: 0;
  box-shadow: 0 0.125rem 0.375rem rgb(0 0 0 / 4%);
  transition: box-shadow 0.15s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 0.25rem 0.75rem rgb(0 0 0 / 8%);
  }

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.primaryColor};
    outline-offset: 0.125rem;
  }
`

// BRAND-9514: checkbox cell shown in selection mode. Rendered empty (layout
// spacer) on decided rows — only pendings are selectable.
export const SelectCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const AvatarBlock = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.grey200Color};
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: ${({ theme }) => theme.grey700Color};
`

export const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

export const ProfileMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

export const PlatformPill = styled.span<{ $onPlatform?: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 1rem;
  padding: 0 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  background: ${({ $onPlatform }) => ($onPlatform ? 'rgba(229, 248, 250, 0.5)' : '#FFF1F1')};
  color: ${({ theme, $onPlatform }) => ($onPlatform ? theme.secondaryColor : '#FC5151')};
`

// Mirrors PlatformPill's shape so it reads as a sibling badge (BRAND-9488);
// reuses the same green already used for approve elsewhere in this file.
export const RosterPill = styled.span`
  display: inline-flex;
  align-items: center;
  height: 1rem;
  padding: 0 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  background: #f00;
  color: #fff;
`

export const IdentityBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-width: 0;
`

export const IdentityTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`

export const HandleLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const ProviderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`

export const DemographicsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

export const GenderBar = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 0.5rem;
  width: 100%;
  height: 0.5rem;
`

export const GenderBarLeftHalf = styled.div`
  display: flex;
  justify-content: flex-end;
`

export const GenderBarRightHalf = styled.div`
  display: flex;
  justify-content: flex-start;
`

export const GenderBarMale = styled.div`
  background: ${({ theme }) => theme.secondary700Color};
  height: 100%;
`

export const GenderBarFemale = styled.div`
  background: ${({ theme }) => theme.primaryColor};
  height: 100%;
`

export const GenderLabels = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 0.5rem;
  font-family: Rubik, verdana, sans-serif;
  font-weight: 400;
  font-size: 0.6875rem;
  line-height: 1rem;
  color: ${({ theme }) => theme.grey700Color};
  margin-top: 0.375rem;

  > span:first-child {
    text-align: right;
  }

  > span:last-child {
    text-align: left;
  }
`

export const DemographicsText = styled.div`
  display: flex;
  gap: 0.25rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey700Color};
  align-items: center;
`

export const AgeCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  min-width: 0;
`

export const AgeTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

export const LocationCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  min-width: 0;
`

export const LocationTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;

  & > :first-child {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  }
`

type ActionVariant = 'reject' | 'approve' | 'undo'

const VARIANT_BG: Record<ActionVariant, string> = {
  reject: 'rgba(255, 241, 241, 0.5)',
  approve: 'rgba(237, 252, 238, 0.5)',
  undo: 'rgba(229, 229, 229, 0.5)',
}

const VARIANT_FG: Record<ActionVariant, string> = {
  reject: '#FC5151',
  approve: '#01D36D',
  undo: '#808080',
}

const VARIANT_HOVER: Record<ActionVariant, string> = {
  reject: '#FC5151',
  approve: '#01D36D',
  undo: '#808080',
}

export const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`

export const IconActionButton = styled.button<{ variant: ActionVariant }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  background: ${({ variant }) => VARIANT_BG[variant]};
  color: ${({ variant }) => VARIANT_FG[variant]};
  transition: background 0.15s ease, color 0.15s ease;

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.primaryColor};
    outline-offset: 0.125rem;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled),
  &:active:not(:disabled) {
    background: ${({ variant }) => VARIANT_HOVER[variant]};
    color: ${({ theme }) => theme.whiteColor};
  }
`
